import { useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { RiPushpin2Fill, RiThumbDownLine, RiThumbUpFill } from "react-icons/ri";
import { toast } from "react-toastify";
import { Badge, Card, CardBody, CardTitle, ListGroup, ListGroupItem } from "reactstrap";
import ComplianceScreeningDashboardService from "src/modules/3rd-party-management/apis/admin/compliance/screening/DashboardService";

const LatestTruePositives = ({ t, filters }) => {
    const fetchDataQuery = useQuery({
        queryKey: [
            "3rd-party-management-fetch-compliance-screening-dashboard",
            "latest-true-positives",
            filters
        ],
        queryFn: async () => {
            const service = ComplianceScreeningDashboardService.getInstance();

            return await service.fetchLatestTruePositives(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        retry: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching dashboard data."), {
                type: "error"
            });
        }
    });

    if (fetchDataQuery.isFetching) {
        return (
            <Card>
                <CardBody>
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '20%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }}></div>
                </CardBody>
            </Card>
        );
    }

    const {
        items = []
    } = fetchDataQuery.data || {};

    return (
        <Card className="h-100">
            <CardBody>
                <CardTitle>
                    {t('Latest True Positives')}
                </CardTitle>
                {items.length > 0 ? (
                    <ListGroup flush>
                        {items.map((item) => (
                            <ListGroupItem
                                key={item.id}
                                className="d-flex align-items-center justify-content-between py-3 mb-2"
                                style={{
                                    borderLeft: '10px solid #FF383C',
                                    borderTopLeftRadius: '10px',
                                    borderBottomLeftRadius: '10px'
                                }}
                            >
                                {/* Left Side */}
                                <div className="d-flex align-items-center gap-3">
                                    <img
                                        src={item.avatarUrl}
                                        alt={item.displayName}
                                        width={52}
                                        height={52}
                                        className="rounded-circle object-fit-cover"
                                    />

                                    <div>
                                        <div className="fw-semibold">
                                            {item.displayName}
                                        </div>

                                        <div className="text-muted small">
                                            {item.source}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side */}
                                <div className="d-flex align-items-center gap-4">
                                    {/* Sanctions */}
                                    <div className="d-flex align-items-center gap-2">
                                        {item.sanctions?.map(
                                            (sanction) => (
                                                <Badge
                                                    key={sanction}
                                                    color="info"
                                                    className="bg-info d-flex align-items-center gap-1 px-2 py-1"
                                                >
                                                    <RiPushpin2Fill
                                                        size={14}
                                                    />

                                                    {sanction}
                                                </Badge>
                                            )
                                        )}
                                    </div>

                                    {/* Status */}
                                    <div className="d-flex flex-column align-items-center">
                                        <div>
                                            {t('True Positive')}
                                        </div>

                                        <div className="d-flex align-items-center justify-content-end gap-2 mt-1">
                                            <RiThumbUpFill
                                                size={18}
                                                className="cursor-pointer text-danger"
                                            />

                                            <RiThumbDownLine
                                                size={18}
                                                className="cursor-pointer text-success"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                ) : (
                    t('No Recent True Positives')
                )}
            </CardBody>
        </Card>
    )
}

export default withNamespaces()(
    LatestTruePositives
);